import styled from 'styled-components';

import { Text } from '../../../../components';
import { COLORS, MOBILE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';

/** Stacks the section's cards (theme · preferred language) with room between them. */
export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** Vertical stack of a card's control groups. */
export const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 4px;
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const GroupHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GroupLabel = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.textStrong};
`;

export const GroupHint = styled(Text)`
  font-size: 13px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

/* ── appearance cards ─────────────────────────────────────────────────────── */

export const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const ModeCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 14px;
  /* Standard grey card + shared hoverWash, matching the rest of the studio's
     clickable cards. Only the selected card carries the orange selection ring;
     unselected keeps a transparent 1px border so selecting never shifts layout. */
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${(p) => (p.$selected ? COLORS.ctaPillBorder : 'transparent')};
  box-shadow: ${(p) => (p.$selected ? `0 0 0 1px ${COLORS.ctaPillBorder}` : 'none')};
  cursor: pointer;
  text-align: left;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease,
    background 150ms ease;
  ${hoverWash}

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaPillBorder};
    outline-offset: 2px;
  }
`;

/* ── mini-studio illustration ─────────────────────────────────────────────────
 * Each appearance tile previews a tiny mock of the actual studio (side rail ·
 * top bar · a stat card with a sparkline · two KPI tiles) drawn in that mode's
 * fixed colours. The Auto tile stacks a light and a dark mock split on a diagonal
 * seam. All colours arrive as transient props from MODE_SWATCH and are exposed to
 * the inner pieces as `--ms-*` CSS variables, so the markup stays palette-agnostic.
 */

/** Rounded, clipped frame that hosts one (light/dark) or two (auto) studio mocks. */
export const PreviewFrame = styled.span`
  position: relative;
  display: block;
  height: 88px;
  border-radius: 9px;
  overflow: hidden;
  border: 1px solid ${COLORS.panelBorder};
`;

type StudioPalette = {
  $bg: string;
  $card: string;
  $rail: string;
  $line: string;
  $text: string;
  $muted: string;
  $accent: string;
  /** Diagonal clip for the dark layer of the Auto tile (right of the seam). */
  $clip?: boolean;
};

/** One full studio mock. Lays the palette down as CSS vars for its children. */
export const MiniStudio = styled.span<StudioPalette>`
  --ms-bg: ${(p) => p.$bg};
  --ms-card: ${(p) => p.$card};
  --ms-rail: ${(p) => p.$rail};
  --ms-line: ${(p) => p.$line};
  --ms-text: ${(p) => p.$text};
  --ms-muted: ${(p) => p.$muted};
  --ms-accent: ${(p) => p.$accent};

  position: absolute;
  inset: 0;
  display: flex;
  background: var(--ms-bg);
  ${(p) => (p.$clip ? 'clip-path: polygon(44% 0, 100% 0, 100% 100%, 56% 100%);' : '')}
`;

/** Left navigation rail with three icon dots (top one is the active/accent item). */
export const Rail = styled.span`
  flex: 0 0 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding-top: 9px;
  background: var(--ms-rail);
  border-right: 1px solid var(--ms-line);
`;

export const RailDot = styled.span<{ $active?: boolean }>`
  width: 7px;
  height: 6px;
  border-radius: 2px;
  background: ${(p) => (p.$active ? 'var(--ms-accent)' : 'var(--ms-muted)')};
  opacity: ${(p) => (p.$active ? 1 : 0.7)};
`;

/** Content column to the right of the rail. */
export const Main = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
`;

/** Top bar: a page-title bar on the left and a small score ring on the right. */
export const TopBar = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TitleBar = styled.span`
  width: 42%;
  height: 5px;
  border-radius: 3px;
  background: var(--ms-text);
`;

export const ScoreRing = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--ms-accent);
  box-sizing: border-box;
`;

/** A raised stat card holding a tiny label bar over a sparkline. */
export const ChartCard = styled.span`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  border-radius: 5px;
  background: var(--ms-card);
  border: 1px solid var(--ms-line);
  color: var(--ms-accent);
  overflow: hidden;
`;

export const ChartLabel = styled.span`
  width: 34%;
  height: 4px;
  border-radius: 2px;
  background: var(--ms-muted);
`;

/** Sparkline SVG; strokes/fills via currentColor (= the card's accent colour). */
export const Spark = styled.svg`
  display: block;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

/** Row of two compact KPI tiles beneath the chart card. */
export const KpiRow = styled.span`
  display: flex;
  gap: 6px;
`;

export const KpiCard = styled.span`
  flex: 1;
  height: 13px;
  display: flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--ms-card);
  border: 1px solid var(--ms-line);
`;

export const KpiBar = styled.span<{ $accent?: boolean }>`
  width: 56%;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => (p.$accent ? 'var(--ms-accent)' : 'var(--ms-muted)')};
`;

/** Hairline seam drawn on the Auto tile's diagonal split (matches the dark clip). */
export const SplitSeam = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const ModeLabelRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ModeLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const ModeHint = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/* ── accent swatches ──────────────────────────────────────────────────────── */

export const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const SwatchButton = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  width: 92px;
  border-radius: 14px;
  /* Standard grey card + shared hoverWash; selected gets the orange ring only. */
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${(p) => (p.$selected ? COLORS.ctaPillBorder : 'transparent')};
  box-shadow: ${(p) => (p.$selected ? `0 0 0 1px ${COLORS.ctaPillBorder}` : 'none')};
  cursor: pointer;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease,
    background 150ms ease;
  ${hoverWash}

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaPillBorder};
    outline-offset: 2px;
  }

  ${MOBILE} {
    flex: 1 1 84px;
    width: auto;
  }
`;

export const Dot = styled.span<{ $color: string; $selected: boolean }>`
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  /* Gap ring matches the card surface (surfaceFaint) so the accent dot reads as a
     clean double ring cut out of the grey card. */
  box-shadow: ${(p) => (p.$selected ? `0 0 0 3px ${COLORS.surfaceFaint}, 0 0 0 5px ${p.$color}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SwatchLabel = styled(Text)<{ $selected: boolean }>`
  font-size: 12px;
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  color: ${(p) => (p.$selected ? COLORS.text : COLORS.textMuted)};
`;

/** Selected check badge floating on a mode card's top-right corner. */
export const Check = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
